import React, { useState, useRef, useEffect } from 'react';
import {
  Settings, LogOut, Moon, Sun, HelpCircle, ChevronDown
} from 'lucide-react';
import './UserMenu.css';

const UserMenu = ({ currentUser, onSettingsClick, onLogout, onThemeToggle, theme = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [open]);

  if (!currentUser) return null;

  const initials = getInitials(currentUser.name, currentUser.email);

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className={`user-menu-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <div className="user-menu-avatar">
          {currentUser.avatar ? (
            <img
              src={currentUser.avatar}
              alt={currentUser.name || 'User'}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span className="user-menu-initials">{initials}</span>
          )}
        </div>
        <ChevronDown
          size={14}
          className={`user-menu-chevron ${open ? 'rotated' : ''}`}
        />
      </button>

      {open && (
        <div className="user-menu-dropdown">
          {/* User Info Header */}
          <div className="user-menu-header">
            <div className="user-menu-header-avatar">
              {currentUser.avatar ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.name || 'User'}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="user-menu-initials">{initials}</span>
              )}
            </div>
            <div className="user-menu-header-info">
              <span className="user-menu-name">
                {currentUser.name || 'User'}
              </span>
              <span className="user-menu-email">{currentUser.email}</span>
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* Menu Items */}
          <button
            className="user-menu-item"
            onClick={() => { setOpen(false); onSettingsClick?.(); }}
          >
            <Settings size={16} />
            <span>Settings</span>
          </button>

          <button
            className="user-menu-item"
            onClick={() => {
              onThemeToggle?.();
              // Don't close menu - let user see the toggle
            }}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>

          <div className="user-menu-divider" />

          <a
            className="user-menu-item"
            href="mailto:support@tekstak.app"
            onClick={() => setOpen(false)}
          >
            <HelpCircle size={16} />
            <span>Help & support</span>
          </a>

          <div className="user-menu-divider" />

          <button
            className="user-menu-item user-menu-item-danger"
            onClick={() => { setOpen(false); onLogout?.(); }}
          >
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
};

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U';
}

export default UserMenu;
